import { useEffect, useState, type FormEvent } from 'react';
import type { CreateTicketTypeGroupInput, TicketTypeGroup } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal } from '../../components/ui/Modal.js';
import {
  useCreateTicketTypeGroup,
  useDeleteTicketTypeGroup,
  useUpdateTicketTypeGroup,
} from './api.js';

interface TicketTypeGroupFormModalProps {
  isOpen: boolean;
  group: TicketTypeGroup | null;
  onClose: () => void;
}

interface FormState {
  name: string;
  sortOrder: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = { name: '', sortOrder: '0', isActive: true };

function toFormState(group: TicketTypeGroup | null): FormState {
  if (!group) {
    return EMPTY_FORM;
  }
  return { name: group.name, sortOrder: String(group.sortOrder), isActive: group.isActive };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function TicketTypeGroupFormModal({
  isOpen,
  group: groupProp,
  onClose,
}: TicketTypeGroupFormModalProps): JSX.Element {
  const [group, setGroup] = useState<TicketTypeGroup | null>(groupProp);
  const isEdit = group !== null;
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(group));
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateTicketTypeGroup();
  const updateMutation = useUpdateTicketTypeGroup();
  const deleteMutation = useDeleteTicketTypeGroup();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setGroup(groupProp);
    }
  }, [isOpen, groupProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(group));
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, group, resetSaveMode, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save ticket type group.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const payload: CreateTicketTypeGroupInput = {
      name: form.name.trim(),
      sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
      isActive: form.isActive,
    };
    if (isEdit) {
      updateMutation.mutate(
        { id: group.id, input: payload },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setGroup(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!group) {
      return;
    }
    deleteMutation.mutate(group.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit ticket type group' : 'New ticket type group'}
        footer={
          <FormActions
            formId="ticket-type-group-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="ticket-type-group-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="Name"
              required
              value={form.name}
              maxLength={100}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              error={fieldErrors.name}
            />
            <TextField
              label="Sort order"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
              error={fieldErrors.sortOrder}
            />
          </div>

          <CheckboxField
            label="Active"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete ticket type group"
        message={`Delete "${group?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
